import { defineStore } from 'pinia'
import {
  displayErrorMessage,
  fetchApi,
  fetchUserData,
  getFetchApiErrorStatus,
  isFetchApiError,
  type UserData,
} from '~/queries'
import { stringSchema } from '~/schemas'

interface State {
  user: UserData | null
}

export const useAuthStore = defineStore('auth', {
  state: (): State => ({
    user: null,
  }),
  getters: {
    isAuthenticated: (state) => !!state.user?.id,
  },
  actions: {
    async fetch() {
      const authToken = useCookie('authToken')

      if (!authToken.value) {
        this.user = null

        return null
      }

      try {
        const data = await fetchUserData()

        this.user = data

        return data
      } catch (error) {
        if (isFetchApiError(error)) {
          if (getFetchApiErrorStatus(error) === 401) {
            authToken.value = null
            this.user = null

            useSessionExpiry().forget()

            return null
          }
        }

        throw createError({
          statusCode: 500,
          statusMessage: 'Error fetching user data',
        })
      }
    },
    async login(body: { email: string; password: string }) {
      const { $toast, $i18n } = useNuxtApp()
      const authToken = useCookie('authToken')
      const modals = useModalsStore()
      const { utmPayload } = useOrderUtm()
      const { devicePayload } = useDeviceSignal()
      const { t } = $i18n

      try {
        // A login is how a returning visitor on a new device tells us who they
        // are, so it carries the touches this device has seen (BOAT-1892) and
        // the device itself (BOAT-1895) — it is the strongest device-to-account
        // link there is, and the one that makes a shared device visible.
        const response: any = await fetchApi(`/login`, {
          method: 'POST',
          body: { ...body, ...utmPayload(), ...(await devicePayload()) },
        })

        const token = stringSchema.min(1).parse(response.token)

        authToken.value = token

        // The login is the one authenticated response with no expiry header to
        // read — there was no token on the request that produced it — so the
        // API puts the moment in the body instead (BOAT-1898). Without it the
        // storefront would be blind to a session it had just opened.
        useSessionExpiry().remember(response.expires_at)

        await nextTick(async () => await this.fetch())

        $toast.success(t('feedback.login.success'))

        modals.closeLoginModal()
      } catch (error) {
        displayErrorMessage(error)
      }
    },
    async logout() {
      const authToken = useCookie('authToken')

      if (!authToken.value) return

      try {
        await fetchApi(`/logout`, {
          method: 'POST',
        })

        authToken.value = null

        useSessionExpiry().forget()

        window.location.reload()
      } catch (error) {
        displayErrorMessage(error)
      }
    },
    /**
     * Ends every session on the account, on every device (BOAT-1898).
     *
     * Worth having because BOAT-1896 gives customers a reason to want it: the
     * new-device mail tells them a sign-in happened that they do not recognise,
     * and until now the only way to act on that was to change the password.
     *
     * It signs this browser out too, which is the point — somebody who cannot
     * tell which session is the stranger's needs all of them gone. Unlike the
     * ordinary logout it navigates rather than reloading, because the customer
     * is standing on an account page that is about to become forbidden and the
     * confirmation is worth more than a blank slate: a reload would throw the
     * message away before it was read.
     */
    async logoutEverywhere() {
      const { $toast, $i18n } = useNuxtApp()
      const authToken = useCookie('authToken')
      const { t } = $i18n

      if (!authToken.value) return

      try {
        await fetchApi(`/logout-everywhere`, {
          method: 'POST',
        })

        authToken.value = null
        this.user = null

        useSessionExpiry().forget()

        $toast.success(t('feedback.logout-everywhere.success'))

        await navigateTo('/')
      } catch (error) {
        displayErrorMessage(error)
      }
    },
    async register(body: {
      first_name: string
      last_name: string
      email: string
      phone?: string | null
      password: string
      password_confirmation: string
    }) {
      const { $toast, $i18n } = useNuxtApp()
      const modals = useModalsStore()
      const { utmPayload } = useOrderUtm()
      const { devicePayload } = useDeviceSignal()
      const { t } = $i18n

      try {
        await fetchApi(`/register`, {
          method: 'POST',
          body: { ...body, ...utmPayload(), ...(await devicePayload()) },
        })

        $toast.success(t('feedback.register.success'))
        this.user = null

        modals.closeRegisterModal()

        await navigateTo('/email-verification')
      } catch (error) {
        displayErrorMessage(error)
      }
    },
    async updateUser(body: {
      first_name?: string
      last_name?: string
      phone?: string | null
      address?: {
        line_1: string
        line_2?: string | null
        city: string
        postcode: string
        country: string
      }
    }) {
      const { $toast, $i18n } = useNuxtApp()
      const authToken = useCookie('authToken')
      const { t } = $i18n

      if (!this.user?.id && !authToken.value) {
        return
      }

      try {
        await fetchApi(`/clients/${this.user?.id}`, {
          method: 'PUT',
          body,
        })

        await nextTick(async () => await this.fetch())

        $toast.success(t('feedback.update-profile.success'))
      } catch (error) {
        displayErrorMessage(error)
      }
    },
    async forgotPassword(body: { email: string }) {
      const { $toast, $i18n } = useNuxtApp()
      const { t } = $i18n

      try {
        await fetchApi(`/forgotten-password`, {
          method: 'POST',
          body,
        })

        $toast.success(t('feedback.reset-password.requested'))
      } catch (error) {
        displayErrorMessage(error)
      }
    },
    async changeEmail(body: {
      email: string
    }) {
      const { $toast, $i18n } = useNuxtApp()
      const authToken = useCookie('authToken')
      const { t } = $i18n

      if (!this.user?.id && !authToken.value) {
        return
      }

      try {
        await fetchApi(`/clients/${this.user?.id}/change-email`, {
          method: 'POST',
          body,
        })

        $toast.success(t('feedback.email-change.toast'))
        return true
      } catch (error) {
        displayErrorMessage(error)
        return false
      }
    },
    async resetPassword(body: {
      token: string
      email: string
      password: string
      password_confirmation: string
    }) {
      try {
        await fetchApi(`/reset-password`, {
          method: 'POST',
          body,
        })
      } catch (error) {
        displayErrorMessage(error)

        throw error
      }
    },
    async updatePassword(body: {
      old_password: string
      password: string
      password_confirmation: string
    }) {
      const authToken = useCookie('authToken')

      if (!this.user?.id || !authToken.value) return

      await fetchApi(`/clients/${this.user.id}/change-password`, {
        method: 'POST',
        body,
      })
    },
  },
})
